import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowLeft,
  Ban,
  CheckCircle2,
  ClipboardList,
  FileSignature,
  FileText,
  RefreshCw,
  Stethoscope,
  UserCircle2,
} from 'lucide-react';
import {
  ProcedureIdSchema,
  UserIdSchema,
  findCatalogEntry,
  resolveConsentUrl,
  type Patient,
  type Procedure,
  type ProcedureId,
  type SignConsentInput,
  type UserId,
} from '@datazo/contracts';
import { useUsersList } from '@/features/users/api';
import { Card, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { AuthenticatedImage } from '@/components/ui/authenticated-image';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { avatarGradient } from '@/components/ui/avatar';
import { SignaturePad } from '@/components/SignaturePad';
import { AuthenticatedPdfFrame } from './AuthenticatedPdfFrame';
import { ApiClientError, downloadAuthenticated } from '@/shared/api/client';
import { ageFromBirthdate, formatShortDateEs, initials } from '@/shared/lib/format';
import { cn } from '@/shared/lib/cn';
import { usePageTitle } from '@/shared/store/page';
import { usePatientById } from '@/features/patients/api';
import { useProcedureById } from '@/features/procedures/api';
import { useToast } from '@/shared/toast/toast-context';
import { useConsentByProcedure, useSignConsent } from './api';

/* -------------------------------------------------------------------------- */
/* Page entry                                                                 */
/* -------------------------------------------------------------------------- */

function isProcedureId(value: string | undefined): value is ProcedureId {
  if (value === undefined) return false;
  return ProcedureIdSchema.safeParse(value).success;
}

export function ConsentSignPage(): JSX.Element {
  const { procedimientoId: param } = useParams<{ procedimientoId: string }>();
  const validatedId = isProcedureId(param) ? param : undefined;

  const procedureQuery = useProcedureById(validatedId);

  usePageTitle('Consentimiento informado', 'Documentos clínicos · Firma del paciente');

  if (validatedId === undefined) {
    return (
      <ErrorState
        title="Identificador inválido"
        detail={`"${param ?? ''}" no es un id de episodio válido.`}
      />
    );
  }

  if (procedureQuery.isLoading) {
    return <Skeleton />;
  }

  if (procedureQuery.isError || procedureQuery.data === undefined) {
    return (
      <ErrorState
        title={
          procedureQuery.isError
            ? 'No fue posible cargar el episodio'
            : 'Episodio no encontrado'
        }
        detail={
          procedureQuery.isError
            ? (procedureQuery.error?.message ?? 'Error inesperado')
            : `El identificador ${validatedId} no existe.`
        }
        onRetry={procedureQuery.isError ? () => void procedureQuery.refetch() : undefined}
      />
    );
  }

  return <ConsentSignLoaded procedure={procedureQuery.data} />;
}

/* -------------------------------------------------------------------------- */
/* Loaded view                                                                */
/* -------------------------------------------------------------------------- */

interface LoadedProps {
  procedure: Procedure;
}

function ConsentSignLoaded({ procedure }: LoadedProps): JSX.Element {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const patientQuery = usePatientById(procedure.pacienteId);
  const consentQuery = useConsentByProcedure(procedure.id);

  const signMutation = useSignConsent();

  // Form state
  const [riesgosExplicados, setRiesgosExplicados] = useState<boolean>(false);
  const [alternativasExplicadas, setAlternativasExplicadas] = useState<boolean>(false);
  const [preguntasResueltas, setPreguntasResueltas] = useState<boolean>(false);
  const [observaciones, setObservaciones] = useState<string>('');
  const [firmaPaciente, setFirmaPaciente] = useState<string | null>(null);
  const [firmaAcompanante, setFirmaAcompanante] = useState<string | null>(null);
  const [nombreAcompanante, setNombreAcompanante] = useState<string>('');
  const [parentescoAcompanante, setParentescoAcompanante] = useState<string>('');
  const [firmaMedico, setFirmaMedico] = useState<string | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);

  const episodeIsOpen = procedure.estado === 'abierto';
  const isAlreadySigned = procedure.docs.consentimiento === 'firmado';
  const isTerminal = procedure.estado === 'cerrado' || procedure.estado === 'anulado';

  // El médico que firma es el ASIGNADO al procedimiento — no el usuario
  // autenticado (que puede ser el admin operando la tablet). El nombre y RM
  // mostrados al lado de la firma corresponden al médico tratante del
  // episodio, igual que el `firmaMedicoId` que se envía al backend.
  const usersQuery = useUsersList({ page: 1, pageSize: 100 });
  const medicoAsignado = useMemo(() => {
    const items = usersQuery.data?.items ?? [];
    return items.find((u) => u.id === procedure.medicoId) ?? null;
  }, [usersQuery.data, procedure.medicoId]);
  const medicoNombre = medicoAsignado?.nombre;

  const medicoUserId = useMemo<UserId | null>(() => {
    const parsed = UserIdSchema.safeParse(procedure.medicoId);
    return parsed.success ? parsed.data : null;
  }, [procedure.medicoId]);

  // El paciente puede tener 2+ procedimientos en el mismo episodio. El
  // consentimiento debe incluir UN documento por cada uno (la enfermera
  // los revisa en pestañas y el paciente firma UNA vez para todos).
  const allTipos = useMemo<string[]>(() => {
    const extras = procedure.tiposAdicionales ?? [];
    return [procedure.tipo, ...extras];
  }, [procedure.tipo, procedure.tiposAdicionales]);

  // URL del preview generado dinámicamente — el PDF unificado de Endomedical
  // con plantilla, datos del paciente/médico/anestesiólogo auto-llenos y
  // firmas pre-cargadas embebidas. Cajas de firma del paciente quedan
  // vacías hasta que firme. Es el MISMO PDF que descargan después de firmar
  // (con la firma capturada agregada), no el viejo PDF estático del catálogo.
  const previewPdfUrl = useMemo(
    () => `/api/consent/by-procedure/${encodeURIComponent(procedure.id)}/pdf/preview`,
    [procedure.id],
  );

  const allConsentDocs = useMemo(
    () =>
      allTipos.map((tipo) => ({
        tipo,
        entry: findCatalogEntry(tipo),
        // Mantenemos `staticUrl` por si necesitamos diagnosticar contra el
        // PDF original, pero `url` apunta al preview generado.
        url: previewPdfUrl,
        staticUrl: resolveConsentUrl(tipo, medicoNombre),
      })),
    [allTipos, medicoNombre, previewPdfUrl],
  );

  const checksOk = riesgosExplicados && alternativasExplicadas && preguntasResueltas;
  const firmaPacienteOk = firmaPaciente !== null && firmaPaciente.length >= 50;
  const firmaAcompananteOk = firmaAcompanante !== null && firmaAcompanante.length >= 50;
  const acompananteDatosOk =
    nombreAcompanante.trim().length >= 2 && parentescoAcompanante.trim().length >= 2;
  // Si el médico tiene firma escaneada pre-cargada en su perfil
  // (`users.firmaUrl`), el PDF la embebe automáticamente y NO se le pide
  // que firme en tablet. La columna `consents.firma_medico` queda vacía y el
  // generador hace fallback a la firma pre-cargada.
  const medicoTieneFirmaPrecargada =
    medicoAsignado !== null &&
    medicoAsignado.firmaUrl !== undefined &&
    medicoAsignado.firmaUrl !== '';
  const firmaMedicoOk =
    medicoTieneFirmaPrecargada || (firmaMedico !== null && firmaMedico.length >= 50);
  // Regla: una de las dos firmas (paciente o acompañante). Si firma el
  // acompañante, también deben venir su nombre y parentesco.
  const firmaPacienteBlockOk =
    firmaPacienteOk || (firmaAcompananteOk && acompananteDatosOk);
  const firmasOk = firmaPacienteBlockOk && firmaMedicoOk;
  const userOk = medicoUserId !== null && patientQuery.data !== undefined;
  const canSubmit = episodeIsOpen && checksOk && firmasOk && userOk && !signMutation.isPending;

  const disabledReason = ((): string | null => {
    if (!episodeIsOpen) {
      return isAlreadySigned
        ? 'Este episodio ya tiene consentimiento firmado.'
        : 'El episodio está cerrado o anulado — no se puede firmar.';
    }
    if (!checksOk) return 'Marca los tres puntos del consentimiento informado.';
    if (!firmaPacienteBlockOk) {
      if (firmaAcompananteOk && !acompananteDatosOk) {
        return 'Diligencia el nombre y parentesco del acompañante que firmó.';
      }
      return 'Debe firmar el paciente o un acompañante autorizado.';
    }
    if (!firmaMedicoOk) {
      return medicoTieneFirmaPrecargada
        ? 'Firma del médico no disponible.'
        : 'Falta la firma del médico tratante.';
    }
    if (medicoUserId === null) return 'No se pudo identificar al médico autenticado.';
    if (patientQuery.data === undefined) return 'Aún se está cargando la información del paciente.';
    return null;
  })();

  const submit = async (): Promise<void> => {
    setServerError(null);
    if (!canSubmit || medicoUserId === null) {
      return;
    }
    const trimmedObs = observaciones.trim();
    const input: SignConsentInput = {
      procedimientoId: procedure.id,
      riesgosExplicados,
      alternativasExplicadas,
      preguntasResueltas,
      ...(trimmedObs === '' ? {} : { observaciones: trimmedObs }),
      ...(firmaPacienteOk && firmaPaciente !== null ? { firmaPaciente } : {}),
      ...(firmaAcompananteOk && firmaAcompanante !== null
        ? {
            firmaAcompanante,
            nombreAcompanante: nombreAcompanante.trim(),
            parentescoAcompanante: parentescoAcompanante.trim(),
          }
        : {}),
      firmaMedicoId: medicoUserId,
      // Solo enviamos `firmaMedico` si la capturamos en tablet. Si el médico
      // tiene firma pre-cargada en `users.firmaUrl`, omitimos el campo y el
      // generador la inyecta automáticamente desde el perfil.
      ...(firmaMedico !== null && firmaMedico.length >= 50 ? { firmaMedico } : {}),
    };
    try {
      await signMutation.mutateAsync(input);
      showToast({
        kind: 'success',
        title: 'Consentimiento firmado',
        description: `El episodio ${procedure.id} avanzó a estado consentimiento.`,
      });
      navigate(`/procedimientos/${procedure.id}`);
    } catch (err) {
      setServerError(buildErrorMessage(err));
      showToast({
        kind: 'danger',
        title: 'No fue posible firmar el consentimiento',
        description: buildErrorMessage(err),
      });
    }
  };

  return (
    <div className="flex flex-col gap-5">
      {/* Top bar */}
      <div className="flex items-center justify-between">
        <Button variant="ghost" size="sm" asChild>
          <Link to={`/procedimientos/${procedure.id}`}>
            <ArrowLeft className="w-4 h-4" /> Volver al episodio
          </Link>
        </Button>
      </div>

      {/* Banner si ya firmado o terminal */}
      {isAlreadySigned && (
        <SignedBanner
          procedureId={procedure.id}
          consent={consentQuery.data ?? null}
          isLoading={consentQuery.isLoading}
          medicoNombre={medicoNombre}
          medicoFirmaUrl={medicoAsignado?.firmaUrl}
        />
      )}
      {!isAlreadySigned && isTerminal && (
        <div className="px-4 py-3 rounded-md bg-danger-soft text-red-800 text-sm border border-red-200 flex items-start gap-2">
          <Ban className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>Episodio terminado — no se puede firmar el consentimiento.</span>
        </div>
      )}

      {/* Card 1: episodio + paciente */}
      <Card>
        <CardHeader>
          <div>
            <CardTitle>Episodio y paciente</CardTitle>
            <CardDescription>Verifica los datos antes de firmar.</CardDescription>
          </div>
          <Badge variant="indigo">{procedure.id}</Badge>
        </CardHeader>
        <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <ProcedureInfo procedure={procedure} />
          <PatientInfo
            isLoading={patientQuery.isLoading}
            isError={patientQuery.isError}
            patient={patientQuery.data}
          />
        </div>
      </Card>

      {/* Solo mostramos el formulario si el episodio está abierto */}
      {episodeIsOpen && (
        <>
          {/* Card: documento(s) del consentimiento (auto-cargado del catálogo).
              Si el episodio tiene múltiples procedimientos, se muestra cada
              PDF en una pestaña — el paciente firma UNA sola vez para todos. */}
          <MultiConsentCard
            documents={allConsentDocs}
            medicoNombre={medicoNombre}
          />

          {/* Card 2: información explicada */}
          <Card>
            <CardHeader>
              <div>
                <CardTitle>Información explicada al paciente</CardTitle>
                <CardDescription>
                  Confirma cada punto antes de proceder con la firma.
                </CardDescription>
              </div>
            </CardHeader>
            <div className="p-6 flex flex-col gap-4">
              <CheckRow
                id="chk-riesgos"
                checked={riesgosExplicados}
                onChange={setRiesgosExplicados}
                label="Los riesgos del procedimiento fueron explicados al paciente"
              />
              <CheckRow
                id="chk-alternativas"
                checked={alternativasExplicadas}
                onChange={setAlternativasExplicadas}
                label="Se explicaron las alternativas terapéuticas"
              />
              <CheckRow
                id="chk-preguntas"
                checked={preguntasResueltas}
                onChange={setPreguntasResueltas}
                label="Las preguntas del paciente fueron resueltas"
              />

              <div className="flex flex-col gap-1.5 mt-2">
                <Label htmlFor="obs">Observaciones (opcional)</Label>
                <textarea
                  id="obs"
                  rows={3}
                  maxLength={2000}
                  value={observaciones}
                  onChange={(e) => setObservaciones(e.target.value)}
                  className="w-full px-3 py-2 rounded-md border border-border bg-surface text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                  placeholder="Notas clínicas relevantes para el consentimiento…"
                />
                <div className="text-[11px] text-text-muted">
                  {observaciones.length}/2000 caracteres
                </div>
              </div>
            </div>
          </Card>

          {/* Card 3: firmas */}
          <Card>
            <CardHeader>
              <div>
                <CardTitle>Firmas</CardTitle>
                <CardDescription>
                  Debe firmar el paciente o, si está impedido (sedación, etc.), un acompañante
                  autorizado. El médico tratante siempre firma.
                </CardDescription>
              </div>
            </CardHeader>
            <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="flex flex-col gap-2">
                <div className="flex items-center justify-between">
                  <Label className="flex items-center gap-2">
                    <UserCircle2 className="w-4 h-4" /> Firma del paciente
                  </Label>
                  {firmaPaciente !== null && (
                    <Badge variant="green">
                      <CheckCircle2 className="w-3 h-3" /> Capturada
                    </Badge>
                  )}
                </div>
                <SignaturePad onChange={setFirmaPaciente} placeholder="Firma del paciente" />
                <div className="text-[11px] text-text-muted">
                  {patientQuery.data !== undefined
                    ? `${patientQuery.data.nombres} ${patientQuery.data.apellidos} · ${patientQuery.data.tipoDoc} ${patientQuery.data.documento}`
                    : '—'}
                </div>
              </div>

              {/* Firma del acompañante (alternativa a la del paciente) */}
              <div className="flex flex-col gap-2">
                <div className="flex items-center justify-between">
                  <Label className="flex items-center gap-2">
                    <UserCircle2 className="w-4 h-4" /> Firma del acompañante
                    <span className="text-[10px] uppercase tracking-wide text-text-muted">
                      (alternativa)
                    </span>
                  </Label>
                  {firmaAcompanante !== null && (
                    <Badge variant="green">
                      <CheckCircle2 className="w-3 h-3" /> Capturada
                    </Badge>
                  )}
                </div>
                <SignaturePad
                  onChange={setFirmaAcompanante}
                  placeholder="Firma del acompañante"
                />
                <div className="grid grid-cols-2 gap-2 mt-1">
                  <div className="flex flex-col gap-1">
                    <Label
                      htmlFor="nombre-acompanante"
                      className="text-[11px] text-text-muted font-normal"
                    >
                      Nombre completo
                    </Label>
                    <input
                      id="nombre-acompanante"
                      type="text"
                      value={nombreAcompanante}
                      onChange={(e) => setNombreAcompanante(e.target.value)}
                      placeholder="Ej. María López Pérez"
                      maxLength={150}
                      className="h-9 px-2.5 rounded-md border border-border bg-surface text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                    />
                  </div>
                  <div className="flex flex-col gap-1">
                    <Label
                      htmlFor="parentesco-acompanante"
                      className="text-[11px] text-text-muted font-normal"
                    >
                      Parentesco
                    </Label>
                    <select
                      id="parentesco-acompanante"
                      value={parentescoAcompanante}
                      onChange={(e) => setParentescoAcompanante(e.target.value)}
                      className="h-9 px-2.5 rounded-md border border-border bg-surface text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                    >
                      <option value="">— Selecciona —</option>
                      <option value="Cónyuge">Cónyuge</option>
                      <option value="Hijo/a">Hijo/a</option>
                      <option value="Padre / Madre">Padre / Madre</option>
                      <option value="Hermano/a">Hermano/a</option>
                      <option value="Tutor legal">Tutor legal</option>
                      <option value="Otro">Otro</option>
                    </select>
                  </div>
                </div>
                {firmaAcompanante !== null && !acompananteDatosOk && (
                  <div className="text-[11px] text-warning">
                    Diligencia nombre y parentesco para validar la firma del acompañante.
                  </div>
                )}
              </div>

              {/* Firma del médico — la pedimos SOLO si no hay firma escaneada
                  pre-cargada en su perfil. Si tiene `firmaUrl`, el PDF la
                  embebe automáticamente y no hace falta capturarla en tablet. */}
              <div className="flex flex-col gap-2">
                {medicoTieneFirmaPrecargada ? (
                  <div className="rounded-md border border-green-200 bg-success-soft p-3 text-sm">
                    <div className="flex items-center gap-2 font-semibold text-green-800">
                      <CheckCircle2 className="w-4 h-4" /> Firma del médico cargada
                      automáticamente
                    </div>
                    {medicoAsignado !== null && (
                      <div className="text-[12px] text-green-800/80 mt-1">
                        Aparecerá embebida en el PDF firmado:{' '}
                        <span className="font-semibold">{medicoAsignado.nombre}</span>
                        {medicoAsignado.registroProfesional !== undefined &&
                          ` · ${medicoAsignado.registroProfesional}`}
                      </div>
                    )}
                  </div>
                ) : (
                  <>
                    <div className="flex items-center justify-between">
                      <Label className="flex items-center gap-2">
                        <Stethoscope className="w-4 h-4" /> Firma del médico tratante
                      </Label>
                      {firmaMedico !== null && (
                        <Badge variant="green">
                          <CheckCircle2 className="w-3 h-3" /> Capturada
                        </Badge>
                      )}
                    </div>
                    <SignaturePad
                      onChange={setFirmaMedico}
                      placeholder="Firma del médico"
                    />
                    {medicoAsignado !== null ? (
                      <div className="text-[11px] text-text-muted flex flex-col gap-0.5">
                        <div className="font-semibold text-text">{medicoAsignado.nombre}</div>
                        <div>
                          {medicoAsignado.especialidad !== undefined &&
                          medicoAsignado.especialidad !== ''
                            ? medicoAsignado.especialidad
                            : 'Sin especialidad registrada'}
                        </div>
                        {medicoAsignado.registroProfesional !== undefined &&
                        medicoAsignado.registroProfesional !== '' ? (
                          <div className="font-mono text-text-soft">
                            {medicoAsignado.registroProfesional}
                          </div>
                        ) : (
                          <div className="text-warning">Sin RM registrado</div>
                        )}
                      </div>
                    ) : usersQuery.isLoading ? (
                      <div className="text-[11px] text-text-muted">Cargando médico…</div>
                    ) : (
                      <div className="text-[11px] text-warning">
                        El médico asignado ({procedure.medicoId}) no se encontró en la base
                        de usuarios.
                      </div>
                    )}
                    {medicoUserId === null && (
                      <div className="text-[11px] text-red-700">
                        El identificador del médico asignado al procedimiento no es válido.
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </Card>

          {/* Card 4: confirmación */}
          <Card>
            <CardHeader>
              <div>
                <CardTitle>Confirmar firma</CardTitle>
                <CardDescription>
                  Al firmar, el episodio transita al estado <strong>consentimiento</strong> y la
                  acción queda registrada en la bitácora.
                </CardDescription>
              </div>
            </CardHeader>
            <div className="p-6 flex flex-col gap-3">
              {serverError !== null && (
                <div className="px-3 py-2 rounded-md bg-danger-soft text-red-800 text-sm border border-red-200 flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>{serverError}</span>
                </div>
              )}

              {disabledReason !== null && (
                <div className="text-xs text-text-muted bg-surface-alt border border-border rounded-md px-3 py-2">
                  {disabledReason}
                </div>
              )}

              <div className="flex flex-wrap gap-2 justify-end">
                <Button
                  variant="secondary"
                  asChild
                  disabled={signMutation.isPending}
                >
                  <Link to={`/procedimientos/${procedure.id}`}>Cancelar</Link>
                </Button>
                <Button
                  onClick={() => void submit()}
                  disabled={!canSubmit}
                  title={disabledReason ?? undefined}
                >
                  {signMutation.isPending ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" /> Firmando…
                    </>
                  ) : (
                    <>
                      <FileSignature className="w-4 h-4" /> Firmar consentimiento
                    </>
                  )}
                </Button>
              </div>
            </div>
          </Card>
        </>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Subcomponents                                                              */
/* -------------------------------------------------------------------------- */

function ProcedureInfo({ procedure }: { procedure: Procedure }): JSX.Element {
  return (
    <div className="flex flex-col gap-2">
      <div className="text-text-muted text-[11px] uppercase tracking-wider font-semibold">
        Procedimiento
      </div>
      <div className="text-sm">
        <div className="font-semibold text-text">{procedure.tipo}</div>
        <div className="text-text-muted">
          {formatShortDateEs(procedure.fecha)} · {procedure.hora}
          {procedure.sala !== undefined && <> · {procedure.sala}</>}
        </div>
      </div>
    </div>
  );
}

interface PatientInfoProps {
  isLoading: boolean;
  isError: boolean;
  patient: Patient | undefined;
}

function PatientInfo({ isLoading, isError, patient }: PatientInfoProps): JSX.Element {
  if (isLoading) {
    return (
      <div className="space-y-2" aria-busy="true">
        <div className="h-4 w-1/2 rounded bg-surface-alt animate-pulse" />
        <div className="h-10 rounded bg-surface-alt animate-pulse" />
      </div>
    );
  }
  if (isError || patient === undefined) {
    return (
      <div className="text-sm text-text-muted">
        No fue posible cargar los datos del paciente.
      </div>
    );
  }
  return (
    <div className="flex items-start gap-3">
      <div
        className={cn(
          'w-12 h-12 rounded-md grid place-items-center text-white font-bold flex-shrink-0',
          avatarGradient(patient.id),
        )}
      >
        {initials(patient.nombres, patient.apellidos)}
      </div>
      <div className="min-w-0 text-sm">
        <div className="font-semibold text-text">
          {patient.nombres} {patient.apellidos}
        </div>
        <div className="text-text-muted text-xs">
          {patient.tipoDoc} {patient.documento} · {ageFromBirthdate(patient.fechaNacimiento)} años ·{' '}
          {patient.sexo === 'F' ? 'Femenino' : patient.sexo === 'M' ? 'Masculino' : 'Otro'}
        </div>
        {patient.alergias !== undefined && patient.alergias !== '' && (
          <div className="mt-2">
            <Badge variant="red">
              <AlertTriangle className="w-3.5 h-3.5" /> Alergias: {patient.alergias}
            </Badge>
          </div>
        )}
      </div>
    </div>
  );
}

interface CheckRowProps {
  id: string;
  checked: boolean;
  onChange(checked: boolean): void;
  label: string;
}

function CheckRow({ id, checked, onChange, label }: CheckRowProps): JSX.Element {
  return (
    <label
      htmlFor={id}
      className="flex items-start gap-3 cursor-pointer p-3 rounded-md border border-border hover:bg-surface-alt"
    >
      <Checkbox
        id={id}
        checked={checked}
        onCheckedChange={(value) => onChange(value === true)}
        className="mt-0.5"
      />
      <span className="text-sm text-text">{label}</span>
    </label>
  );
}

interface SignedBannerProps {
  procedureId: ProcedureId;
  consent: import('@datazo/contracts').Consent | null;
  isLoading: boolean;
  medicoNombre?: string;
  medicoFirmaUrl?: string;
}

function SignedBanner({
  procedureId,
  consent,
  isLoading,
  medicoNombre,
  medicoFirmaUrl,
}: SignedBannerProps): JSX.Element {
  const [open, setOpen] = useState<boolean>(false);
  const [downloading, setDownloading] = useState<boolean>(false);
  const { showToast } = useToast();
  const pdfUrl = `/api/consent/by-procedure/${encodeURIComponent(procedureId)}/pdf`;

  const handleDownload = async (): Promise<void> => {
    setDownloading(true);
    try {
      await downloadAuthenticated(pdfUrl, `consentimiento-${procedureId}.pdf`);
    } catch (err) {
      showToast({
        kind: 'danger',
        title: 'No se pudo descargar el PDF',
        description:
          err instanceof ApiClientError && err.status === 401
            ? 'Tu sesión expiró. Vuelve a iniciar sesión.'
            : 'Intenta de nuevo en unos segundos.',
      });
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="rounded-md bg-success-soft border border-green-200 text-green-800">
      <div className="px-4 py-3 flex items-start gap-3 flex-wrap">
        <CheckCircle2 className="w-5 h-5 mt-0.5 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-sm">
            Este episodio ya tiene consentimiento firmado
          </div>
          <div className="text-xs text-green-700 mt-0.5">
            La firma quedó registrada en la bitácora y el documento es inalterable.
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => void handleDownload()}
            disabled={downloading}
          >
            <FileText className="w-4 h-4" />{' '}
            {downloading ? 'Generando PDF…' : 'Descargar PDF firmado'}
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setOpen((v) => !v)}
            disabled={isLoading}
          >
            {open ? 'Ocultar' : 'Ver detalle'}
          </Button>
        </div>
      </div>
      {open && (
        <div className="border-t border-green-200 px-4 py-3 bg-white/60">
          {isLoading ? (
            <div className="text-xs text-text-muted">Cargando consentimiento…</div>
          ) : consent === null ? (
            <div className="text-xs text-text-muted">
              No se pudo obtener el detalle del consentimiento.
            </div>
          ) : (
            <SignedDetail
              consent={consent}
              medicoNombre={medicoNombre}
              medicoFirmaUrl={medicoFirmaUrl}
            />
          )}
        </div>
      )}
    </div>
  );
}

function SignedDetail({
  consent,
  medicoNombre,
  medicoFirmaUrl,
}: {
  consent: import('@datazo/contracts').Consent;
  medicoNombre?: string;
  medicoFirmaUrl?: string;
}): JSX.Element {
  // La firma del médico puede venir capturada en el consentimiento (data URL) o,
  // lo más común, estar pre-cargada en su perfil (`users.firmaUrl`) — en ese
  // caso NO es un data URL sino una ruta de archivo, y la cargamos vía endpoint
  // autenticado (`/api/users/:id/firma`) con AuthenticatedImage.
  const medicoTitle = medicoNombre !== undefined ? `Médico · ${medicoNombre}` : 'Médico';
  const firmaMedicoCapturada =
    consent.firmaMedico !== undefined && consent.firmaMedico !== '' ? consent.firmaMedico : null;
  const medicoTieneFirmaPrecargada = medicoFirmaUrl !== undefined && medicoFirmaUrl !== '';
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-text">
      <div className="flex flex-col gap-1">
        <div className="text-[11px] uppercase tracking-wider font-semibold text-text-muted">
          Firmado en
        </div>
        <div>
          {new Date(consent.firmadoEn).toLocaleString('es-CO', {
            dateStyle: 'long',
            timeStyle: 'short',
          })}
        </div>
        <div className="text-[11px] uppercase tracking-wider font-semibold text-text-muted mt-2">
          Médico
        </div>
        <div>{medicoNombre ?? consent.firmaMedicoId}</div>
        <div className="text-[11px] uppercase tracking-wider font-semibold text-text-muted mt-2">
          Confirmaciones
        </div>
        <ul className="list-disc pl-5 text-xs">
          <li>Riesgos {consent.riesgosExplicados ? 'explicados' : 'no marcados'}</li>
          <li>Alternativas {consent.alternativasExplicadas ? 'explicadas' : 'no marcadas'}</li>
          <li>Preguntas {consent.preguntasResueltas ? 'resueltas' : 'no marcadas'}</li>
        </ul>
        {consent.observaciones !== undefined && consent.observaciones !== '' && (
          <>
            <div className="text-[11px] uppercase tracking-wider font-semibold text-text-muted mt-2">
              Observaciones
            </div>
            <div className="text-xs whitespace-pre-wrap">{consent.observaciones}</div>
          </>
        )}
      </div>
      <div className="grid grid-cols-2 gap-3">
        {consent.firmaPaciente !== undefined && (
          <SignaturePreview title="Paciente" dataUrl={consent.firmaPaciente} />
        )}
        {consent.firmaAcompanante !== undefined && (
          <SignaturePreview
            title={`Acompañante${
              consent.nombreAcompanante !== undefined ? ` · ${consent.nombreAcompanante}` : ''
            }${
              consent.parentescoAcompanante !== undefined
                ? ` (${consent.parentescoAcompanante})`
                : ''
            }`}
            dataUrl={consent.firmaAcompanante}
          />
        )}
        {firmaMedicoCapturada !== null ? (
          <SignaturePreview title={medicoTitle} dataUrl={firmaMedicoCapturada} />
        ) : (
          <div className="flex flex-col gap-1">
            <div className="text-[11px] uppercase tracking-wider font-semibold text-text-muted">
              {medicoTitle}
            </div>
            <div className="bg-white border border-border rounded-md p-2 grid place-items-center min-h-[80px]">
              {medicoTieneFirmaPrecargada ? (
                <AuthenticatedImage
                  url={`/api/users/${encodeURIComponent(consent.firmaMedicoId)}/firma`}
                  alt={`Firma de ${medicoTitle}`}
                  className="max-h-24 object-contain"
                  fallback={
                    <span className="text-[11px] text-text-muted">
                      Firma pre-cargada (visible en el PDF)
                    </span>
                  }
                />
              ) : (
                <span className="text-[11px] text-text-muted">Sin firma registrada</span>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function SignaturePreview({
  title,
  dataUrl,
}: {
  title: string;
  dataUrl: string;
}): JSX.Element {
  return (
    <div className="flex flex-col gap-1">
      <div className="text-[11px] uppercase tracking-wider font-semibold text-text-muted">
        {title}
      </div>
      <div className="bg-white border border-border rounded-md p-2 grid place-items-center min-h-[80px]">
        {dataUrl !== '' ? (
          <img src={dataUrl} alt={`Firma de ${title}`} className="max-h-24 object-contain" />
        ) : (
          <span className="text-[11px] text-text-muted">Sin firma registrada</span>
        )}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Error / skeleton                                                            */
/* -------------------------------------------------------------------------- */

function Skeleton(): JSX.Element {
  return (
    <div className="flex flex-col gap-5">
      <Button variant="ghost" size="sm" asChild>
        <Link to="/procedimientos">
          <ArrowLeft className="w-4 h-4" /> Volver
        </Link>
      </Button>
      <Card>
        <div className="p-6 space-y-3" aria-busy="true">
          <div className="h-5 w-1/3 rounded bg-surface-alt animate-pulse" />
          <div className="h-20 rounded bg-surface-alt animate-pulse" />
        </div>
      </Card>
      <Card>
        <div className="p-6 space-y-3" aria-busy="true">
          <div className="h-5 w-1/3 rounded bg-surface-alt animate-pulse" />
          <div className="h-32 rounded bg-surface-alt animate-pulse" />
        </div>
      </Card>
    </div>
  );
}

interface ErrorStateProps {
  title: string;
  detail: string;
  onRetry?(): void;
}

function ErrorState({ title, detail, onRetry }: ErrorStateProps): JSX.Element {
  return (
    <div className="flex flex-col gap-5">
      <Button variant="ghost" size="sm" asChild>
        <Link to="/procedimientos">
          <ArrowLeft className="w-4 h-4" /> Volver
        </Link>
      </Button>
      <Card>
        <div className="p-8 text-center">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-danger-soft text-danger mb-3">
            <ClipboardList className="w-6 h-6" />
          </div>
          <div className="text-text font-bold text-base mb-1">{title}</div>
          <p className="text-text-muted text-sm mb-4">{detail}</p>
          <div className="flex justify-center gap-2">
            {onRetry !== undefined && (
              <Button variant="secondary" onClick={onRetry}>
                <RefreshCw className="w-4 h-4" /> Reintentar
              </Button>
            )}
            <Button variant="secondary" asChild>
              <Link to="/procedimientos">
                <ArrowLeft className="w-4 h-4" /> Volver al listado
              </Link>
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Helpers                                                                    */
/* -------------------------------------------------------------------------- */

function buildErrorMessage(err: unknown): string {
  if (err instanceof ApiClientError) {
    if (err.status === 403) {
      return 'No tienes permiso para firmar este consentimiento como el médico identificado.';
    }
    if (err.status === 404) {
      return 'Episodio no encontrado.';
    }
    if (err.status === 409) {
      return err.message !== ''
        ? err.message
        : 'El consentimiento no puede firmarse en el estado actual del episodio.';
    }
    if (err.status === 401) {
      return 'Sesión expirada. Vuelve a iniciar sesión.';
    }
    if (err.status === 422 || err.status === 400) {
      return 'Datos inválidos. Revisa los campos del formulario.';
    }
    if (err.status >= 500) {
      return 'Error del servidor. Inténtalo nuevamente.';
    }
    return err.message;
  }
  return 'No fue posible procesar la solicitud.';
}

/* -------------------------------------------------------------------------- */
/* MultiConsentCard — N documentos del episodio en pestañas                   */
/* -------------------------------------------------------------------------- */

interface ConsentDocEntry {
  tipo: string;
  entry: ReturnType<typeof findCatalogEntry>;
  url: string | null;
}

interface MultiConsentCardProps {
  documents: ConsentDocEntry[];
  medicoNombre: string | undefined;
}

function MultiConsentCard({ documents, medicoNombre }: MultiConsentCardProps): JSX.Element {
  const [activeIdx, setActiveIdx] = useState<number>(0);
  if (documents.length === 0) return <></>;
  if (documents.length === 1) {
    const d = documents[0]!;
    return (
      <ConsentDocumentCard
        tipo={d.tipo}
        cups={d.entry?.cups}
        url={d.url}
        medicoNombre={medicoNombre}
        requiereMedicoEspecifico={d.entry?.consentimiento.kind === 'especifico_medico'}
      />
    );
  }
  const active = documents[activeIdx] ?? documents[0]!;
  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-primary" />
              Consentimientos del episodio ({documents.length})
            </CardTitle>
            <CardDescription>
              Este episodio incluye varios procedimientos. Lee cada consentimiento con el paciente —
              la firma de abajo cubre todos.
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <div className="px-6 pt-1 flex flex-wrap gap-1.5">
        {documents.map((d, idx) => (
          <button
            key={`${d.tipo}-${idx}`}
            type="button"
            onClick={() => setActiveIdx(idx)}
            className={
              idx === activeIdx
                ? 'px-3 py-1.5 rounded-md text-xs font-semibold bg-primary text-white border border-primary'
                : 'px-3 py-1.5 rounded-md text-xs font-semibold bg-surface text-text-soft border border-border hover:border-primary hover:text-primary'
            }
            title={d.tipo}
          >
            {d.entry !== undefined ? `[${d.entry.cups}] ` : ''}
            {d.tipo.length > 50 ? `${d.tipo.slice(0, 50)}…` : d.tipo}
          </button>
        ))}
      </div>
      <div className="p-6 pt-3">
        <ConsentDocumentCardBody
          tipo={active.tipo}
          cups={active.entry?.cups}
          url={active.url}
          medicoNombre={medicoNombre}
          requiereMedicoEspecifico={active.entry?.consentimiento.kind === 'especifico_medico'}
        />
      </div>
    </Card>
  );
}

/* -------------------------------------------------------------------------- */
/* ConsentDocumentCard — muestra el PDF del consentimiento del catálogo       */
/* -------------------------------------------------------------------------- */

interface ConsentDocumentCardProps {
  tipo: string;
  cups: string | undefined;
  url: string | null;
  medicoNombre: string | undefined;
  requiereMedicoEspecifico: boolean;
}

function ConsentDocumentCard({
  tipo,
  cups,
  url,
  medicoNombre,
  requiereMedicoEspecifico,
}: ConsentDocumentCardProps): JSX.Element {
  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-primary" />
              Documento del consentimiento
            </CardTitle>
            <CardDescription>
              {cups !== undefined ? `CUPS ${cups} · ` : ''}
              {tipo}
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <div className="p-6 pt-0">
        <ConsentDocumentCardBody
          tipo={tipo}
          cups={cups}
          url={url}
          medicoNombre={medicoNombre}
          requiereMedicoEspecifico={requiereMedicoEspecifico}
        />
      </div>
    </Card>
  );
}

/** Body separado para poder reutilizar dentro de `MultiConsentCard` (tabs). */
function ConsentDocumentCardBody({
  tipo,
  cups,
  url,
  medicoNombre,
  requiereMedicoEspecifico,
}: ConsentDocumentCardProps): JSX.Element {
  return (
    <>
      <div className="mb-3 flex items-center justify-between gap-2 flex-wrap">
        <div className="text-[12px] text-text-muted">
          {cups !== undefined ? `CUPS ${cups} · ` : ''}
          {tipo}
        </div>
      </div>
      {url === null && requiereMedicoEspecifico ? (
        <div className="flex items-start gap-2 rounded-md border border-warning bg-warning-soft p-4">
          <AlertTriangle className="w-5 h-5 text-warning mt-0.5 flex-shrink-0" />
          <div className="text-sm">
            <div className="font-semibold text-text">
              Este procedimiento requiere un médico tratante específico.
            </div>
            <div className="text-text-muted mt-1">
              El médico asignado al episodio
              {medicoNombre !== undefined ? ` (${medicoNombre})` : ''} no coincide con ninguna de
              las plantillas de consentimiento disponibles. Edita el procedimiento y asigna el
              médico correcto antes de firmar.
            </div>
          </div>
        </div>
      ) : url === null ? (
        <div className="flex items-start gap-2 rounded-md border border-border bg-surface-alt p-4">
          <AlertTriangle className="w-5 h-5 text-text-muted mt-0.5 flex-shrink-0" />
          <div className="text-sm">
            <div className="font-semibold text-text">
              Este procedimiento no requiere consentimiento médico firmado.
            </div>
            <div className="text-text-muted mt-1">
              Continúa con los demás documentos del episodio.
            </div>
          </div>
        </div>
      ) : (
        <div className="rounded-md border border-border overflow-hidden bg-surface-alt">
          {/* El preview es un endpoint protegido del API, no un archivo
              estático. Usamos AuthenticatedPdfFrame para descargarlo con
              `Authorization: Bearer` y servirlo al iframe como `blob:`. */}
          <AuthenticatedPdfFrame
            url={url}
            title={`Consentimiento médico — ${tipo}`}
            className="w-full"
            style={{ height: '620px', border: 'none' }}
          />
        </div>
      )}
    </>
  );
}

export default ConsentSignPage;
